/**
 * Calling a model directly from the app instead of through the `ai` function on
 * your Supabase project (Nexus Open, 8c). Each adapter turns a request into the
 * same Server-Sent Events the function sends ({@link AiEvent}), so the
 * assistant, writing help, task extraction, summary and plugin builder work
 * unchanged with any provider.
 *
 * - Anthropic: Claude with your own API key.
 * - OpenAI-compatible: OpenAI, Azure, OpenRouter, Mistral, Groq, Google Gemini
 *   (its OpenAI endpoint), and local servers (Ollama, LM Studio) without a key.
 *
 * The conversation stays in Anthropic's shape (what the rest of the app keeps);
 * the OpenAI adapter converts on the way out and back.
 */
import Anthropic from '@anthropic-ai/sdk';
import {
  FALLBACK_PRICE,
  MODES,
  PRICES,
  type ToolDef,
} from '../../../supabase/functions/_shared/prompts';
import type {
  AiErrorCode,
  AiEvent,
  AiRequest,
  ContentBlock,
  ContentBlockParam,
  MessageParam,
  StopReason,
} from './protocol';

export type ProviderKind = 'anthropic' | 'openai';

export interface DirectModel {
  kind: ProviderKind;
  /** OpenAI-compatible: the API root, e.g. https://api.openai.com/v1 or http://localhost:11434/v1. */
  baseUrl?: string;
  /** Empty for local servers. */
  apiKey?: string;
  model: string;
  /** The model can call tools: needed for the assistant's actions and the plugin builder. */
  tools: boolean;
  /** US dollars per million tokens (input, output); unknown → no cost shown. */
  price?: [number, number];
  /** Fetch to use (the desktop app passes one without CORS limits). */
  fetch?: typeof fetch;
  /** Book this request's cost; returns the month's total and limit (0 = none). */
  account?: (costUsd: number) => { monthUsd: number; limitUsd: number };
}

const usageOf = (model: DirectModel, costUsd: number) => ({
  costUsd,
  ...(model.account?.(costUsd) ?? { monthUsd: costUsd, limitUsd: 0 }),
});

/** Ready-made providers for the settings. */
export const PRESETS: {
  id: string;
  kind: ProviderKind;
  baseUrl?: string;
  needsKey: boolean;
  local?: boolean;
  models: string[];
}[] = [
  {
    id: 'anthropic',
    kind: 'anthropic',
    needsKey: true,
    models: ['claude-sonnet-5-5', 'claude-opus-5-5', 'claude-haiku-5-5'],
  },
  {
    id: 'openai',
    kind: 'openai',
    baseUrl: 'https://api.openai.com/v1',
    needsKey: true,
    models: ['gpt-5.5', 'gpt-5.5-mini'],
  },
  {
    id: 'gemini',
    kind: 'openai',
    baseUrl: 'https://generativelanguage.googleapis.com/v1beta/openai',
    needsKey: true,
    models: ['gemini-3.5-pro', 'gemini-3.5-flash'],
  },
  {
    id: 'openrouter',
    kind: 'openai',
    baseUrl: 'https://openrouter.ai/api/v1',
    needsKey: true,
    models: [],
  },
  {
    id: 'mistral',
    kind: 'openai',
    baseUrl: 'https://api.mistral.ai/v1',
    needsKey: true,
    models: [],
  },
  {
    id: 'groq',
    kind: 'openai',
    baseUrl: 'https://api.groq.com/openai/v1',
    needsKey: true,
    models: [],
  },
  {
    id: 'ollama',
    kind: 'openai',
    baseUrl: 'http://localhost:11434/v1',
    needsKey: false,
    local: true,
    models: [],
  },
  {
    id: 'lmstudio',
    kind: 'openai',
    baseUrl: 'http://localhost:1234/v1',
    needsKey: false,
    local: true,
    models: [],
  },
  { id: 'custom', kind: 'openai', needsKey: false, models: [] },
];

/** A server on this computer: nothing leaves it, and it costs nothing. */
export function isLocalUrl(url: string | undefined): boolean {
  if (!url) return false;
  try {
    const host = new URL(url).hostname;
    return (
      host === 'localhost' ||
      host === '127.0.0.1' ||
      host === '[::1]' ||
      host.endsWith('.localhost')
    );
  } catch {
    return false;
  }
}

const ONE_M = 1_000_000;

function costOf(model: DirectModel, input: number, output: number): number {
  if (model.kind === 'openai' && isLocalUrl(model.baseUrl)) return 0;
  const price =
    model.price ??
    (model.kind === 'anthropic'
      ? ((p) => [p[0], p[1]] as [number, number])(
          PRICES.find(([prefix]) => model.model.startsWith(prefix))?.[1] ?? FALLBACK_PRICE,
        )
      : null);
  return price ? (input * price[0] + output * price[1]) / ONE_M : 0;
}

/** A Response streaming `data: <event>` lines, like the `ai` function's. */
export function eventResponse(events: AsyncIterable<AiEvent>): Response {
  const encoder = new TextEncoder();
  const stream = new ReadableStream<Uint8Array>({
    async start(controller) {
      try {
        for await (const e of events)
          controller.enqueue(encoder.encode(`data: ${JSON.stringify(e)}\n\n`));
      } catch (e) {
        const error: AiEvent = {
          type: 'error',
          error: e instanceof ProviderError ? e.code : 'network',
          message: e instanceof Error ? e.message : String(e),
        };
        controller.enqueue(encoder.encode(`data: ${JSON.stringify(error)}\n\n`));
      } finally {
        controller.close();
      }
    },
  });
  return new Response(stream, { headers: { 'Content-Type': 'text/event-stream' } });
}

export class ProviderError extends Error {
  constructor(
    readonly code: AiErrorCode,
    message?: string,
  ) {
    super(message ?? code);
  }
}

function httpError(status: number, body: string): ProviderError {
  const code: AiErrorCode =
    status === 401 || status === 403
      ? 'bad-key'
      : status === 402 || /credit|quota|billing|insufficient/i.test(body)
        ? 'no-credit'
        : status === 429
          ? 'rate-limit'
          : status === 503 || status === 529
            ? 'overloaded'
            : status >= 400 && status < 500
              ? 'bad-request'
              : 'api';
  return new ProviderError(code, `HTTP ${status}: ${body.slice(0, 300)}`);
}

function needTools(req: AiRequest, model: DirectModel): ToolDef[] | undefined {
  const tools = MODES[req.mode].tools;
  if (!tools) return undefined;
  if (model.tools) return tools;
  // The builder only answers with a tool call; the assistant can do without.
  if (req.mode === 'plugin')
    throw new ProviderError('bad-request', `${model.model} cannot call tools`);
  return undefined;
}

// ---------------------------------------------------------------------------
// Anthropic

function anthropicCode(e: unknown): AiErrorCode {
  if (e instanceof Anthropic.RateLimitError) return 'rate-limit';
  if (e instanceof Anthropic.BadRequestError && /credit balance/i.test(e.message))
    return 'no-credit';
  if (e instanceof Anthropic.BadRequestError) return 'bad-request';
  if (e instanceof Anthropic.AuthenticationError || e instanceof Anthropic.PermissionDeniedError)
    return 'bad-key';
  if (e instanceof Anthropic.APIError && (e.status === 529 || e.status === 503))
    return 'overloaded';
  if (e instanceof Anthropic.APIConnectionError) return 'network';
  return 'api';
}

export async function* anthropicEvents(
  model: DirectModel,
  req: AiRequest,
  signal?: AbortSignal,
): AsyncGenerator<AiEvent> {
  const mode = MODES[req.mode];
  const tools = needTools(req, model);
  const client = new Anthropic({
    apiKey: model.apiKey ?? '',
    ...(model.baseUrl ? { baseURL: model.baseUrl } : {}),
    dangerouslyAllowBrowser: true,
    maxRetries: 2,
    ...(model.fetch ? { fetch: model.fetch } : {}),
  });
  try {
    const answer = client.beta.messages.stream(
      {
        model: model.model,
        max_tokens: mode.maxTokens ?? 16_000,
        thinking: { type: 'adaptive' },
        output_config: { effort: mode.effort, ...(mode.format ? { format: mode.format } : {}) },
        cache_control: { type: 'ephemeral' },
        system: mode.system,
        ...(tools ? { tools: tools as Anthropic.Beta.Messages.BetaTool[] } : {}),
        messages: req.messages,
      },
      signal ? { signal } : {},
    );
    for await (const event of answer) {
      if (event.type === 'content_block_delta' && event.delta.type === 'text_delta')
        yield { type: 'text', text: event.delta.text };
      else if (event.type === 'content_block_start' && event.content_block.type === 'tool_use')
        yield { type: 'tool', name: event.content_block.name };
    }
    const message = await answer.finalMessage();
    const costUsd = costOf(
      model,
      message.usage.input_tokens +
        (message.usage.cache_read_input_tokens ?? 0) * 0.1 +
        (message.usage.cache_creation_input_tokens ?? 0) * 1.25,
      message.usage.output_tokens,
    );
    yield {
      type: 'done',
      content: message.content,
      stop_reason: message.stop_reason,
      usage: usageOf(model, costUsd),
    };
  } catch (e) {
    if (signal?.aborted) throw e;
    throw new ProviderError(anthropicCode(e), e instanceof Error ? e.message : String(e));
  }
}

// ---------------------------------------------------------------------------
// OpenAI-compatible

type OaMessage =
  | { role: 'system' | 'user'; content: string }
  | {
      role: 'assistant';
      content: string | null;
      tool_calls?: {
        id: string;
        type: 'function';
        function: { name: string; arguments: string };
      }[];
    }
  | { role: 'tool'; tool_call_id: string; content: string };

const textOf = (content: string | ContentBlockParam[] | unknown): string =>
  typeof content === 'string'
    ? content
    : Array.isArray(content)
      ? content
          .map((b: { type?: string; text?: string }) => (b.type === 'text' ? (b.text ?? '') : ''))
          .join('\n')
      : '';

/** Anthropic-shaped conversation → OpenAI chat messages (thinking blocks are left out). */
export function toOpenAiMessages(system: string, messages: MessageParam[]): OaMessage[] {
  const out: OaMessage[] = [{ role: 'system', content: system }];
  for (const m of messages) {
    if (typeof m.content === 'string') {
      out.push({ role: m.role, content: m.content });
      continue;
    }
    if (m.role === 'assistant') {
      const text = m.content
        .filter((b) => b.type === 'text')
        .map((b) => (b as { text: string }).text)
        .join('');
      const calls = m.content
        .filter((b) => b.type === 'tool_use')
        .map((b) => {
          const u = b as { id: string; name: string; input: unknown };
          return {
            id: u.id,
            type: 'function' as const,
            function: { name: u.name, arguments: JSON.stringify(u.input ?? {}) },
          };
        });
      out.push({
        role: 'assistant',
        content: text || null,
        ...(calls.length ? { tool_calls: calls } : {}),
      });
      continue;
    }
    // A user turn: tool results become tool messages (first), the rest one user message.
    const texts: string[] = [];
    for (const b of m.content) {
      if (b.type === 'tool_result') {
        const r = b as { tool_use_id: string; content?: unknown; is_error?: boolean };
        out.push({
          role: 'tool',
          tool_call_id: r.tool_use_id,
          content: (r.is_error ? 'Error: ' : '') + textOf(r.content),
        });
      } else if (b.type === 'text') texts.push((b as { text: string }).text);
    }
    if (texts.length) out.push({ role: 'user', content: texts.join('\n\n') });
  }
  return out;
}

export function toOpenAiTools(tools: ToolDef[]) {
  return tools.map((t) => ({
    type: 'function' as const,
    function: { name: t.name, description: t.description, parameters: t.input_schema },
  }));
}

/** Server-Sent Events lines from a fetch response (`data: …`, `[DONE]` ends). */
async function* sseData(res: Response): AsyncGenerator<string> {
  if (!res.body) return;
  const reader = res.body.pipeThrough(new TextDecoderStream()).getReader();
  let buffer = '';
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += value;
    let nl: number;
    while ((nl = buffer.indexOf('\n')) >= 0) {
      const line = buffer.slice(0, nl).trim();
      buffer = buffer.slice(nl + 1);
      if (!line.startsWith('data:')) continue;
      const data = line.slice(5).trim();
      if (data === '[DONE]') return;
      if (data) yield data;
    }
  }
}

interface OaChunk {
  choices?: {
    delta?: {
      content?: string | null;
      tool_calls?: {
        index: number;
        id?: string;
        function?: { name?: string; arguments?: string };
      }[];
    };
    finish_reason?: string | null;
  }[];
  usage?: { prompt_tokens?: number; completion_tokens?: number } | null;
  error?: { message?: string };
}

export async function* openAiEvents(
  model: DirectModel,
  req: AiRequest,
  signal?: AbortSignal,
): AsyncGenerator<AiEvent> {
  const mode = MODES[req.mode];
  const tools = needTools(req, model);
  const base = (model.baseUrl ?? '').replace(/\/+$/, '');
  if (!base) throw new ProviderError('not-configured', 'No address for this provider');
  const body = {
    model: model.model,
    stream: true,
    stream_options: { include_usage: true },
    max_tokens: mode.maxTokens ?? 16_000,
    messages: toOpenAiMessages(mode.system, req.messages),
    ...(tools ? { tools: toOpenAiTools(tools) } : {}),
    ...(mode.format
      ? {
          response_format: {
            type: 'json_schema',
            json_schema: { name: `nexus_${req.mode}`, strict: true, schema: mode.format.schema },
          },
        }
      : {}),
  };
  const doFetch = model.fetch ?? fetch;
  let res: Response;
  try {
    res = await doFetch(`${base}/chat/completions`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(model.apiKey ? { Authorization: `Bearer ${model.apiKey}` } : {}),
      },
      body: JSON.stringify(body),
      ...(signal ? { signal } : {}),
    });
  } catch (e) {
    if (signal?.aborted) throw e;
    throw new ProviderError('network', e instanceof Error ? e.message : String(e));
  }
  if (!res.ok) throw httpError(res.status, await res.text().catch(() => ''));

  let text = '';
  const calls: { id: string; name: string; args: string }[] = [];
  let finish: string | null = null;
  let usage = { input: 0, output: 0 };
  for await (const data of sseData(res)) {
    let chunk: OaChunk;
    try {
      chunk = JSON.parse(data) as OaChunk;
    } catch {
      continue;
    }
    if (chunk.error) throw new ProviderError('api', chunk.error.message);
    if (chunk.usage)
      usage = { input: chunk.usage.prompt_tokens ?? 0, output: chunk.usage.completion_tokens ?? 0 };
    const choice = chunk.choices?.[0];
    if (!choice) continue;
    if (choice.delta?.content) {
      text += choice.delta.content;
      yield { type: 'text', text: choice.delta.content };
    }
    for (const tc of choice.delta?.tool_calls ?? []) {
      let call = calls[tc.index];
      if (!call) {
        call = calls[tc.index] = { id: tc.id ?? `call_${tc.index}`, name: '', args: '' };
      }
      if (tc.id) call.id = tc.id;
      if (tc.function?.name) {
        const first = !call.name;
        call.name += tc.function.name;
        if (first) yield { type: 'tool', name: call.name };
      }
      if (tc.function?.arguments) call.args += tc.function.arguments;
    }
    if (choice.finish_reason) finish = choice.finish_reason;
  }

  const content: ContentBlock[] = [];
  if (text) content.push({ type: 'text', text, citations: null } as ContentBlock);
  for (const c of calls.filter(Boolean)) {
    // Unparseable arguments stay null: runTurn reports an incomplete tool call.
    let input: unknown;
    try {
      input = c.args ? JSON.parse(c.args) : {};
    } catch {
      input = null;
    }
    content.push({ type: 'tool_use', id: c.id, name: c.name, input } as ContentBlock);
  }
  const stop: StopReason =
    calls.length > 0 || finish === 'tool_calls'
      ? 'tool_use'
      : finish === 'length'
        ? 'max_tokens'
        : finish === 'content_filter'
          ? 'refusal'
          : 'end_turn';
  const costUsd = costOf(model, usage.input, usage.output);
  yield {
    type: 'done',
    content,
    stop_reason: stop,
    usage: usageOf(model, costUsd),
  };
}

/** A transport that calls the model directly (see the `Transport` type in chat.ts). */
export function directTransport(model: DirectModel) {
  return async (req: AiRequest, signal?: AbortSignal): Promise<Response> =>
    eventResponse(
      model.kind === 'anthropic'
        ? anthropicEvents(model, req, signal)
        : openAiEvents(model, req, signal),
    );
}

/** Names of the models a provider offers (OpenAI-compatible `/models`); empty when it cannot tell. */
export async function listModels(
  baseUrl: string,
  apiKey: string | undefined,
  doFetch: typeof fetch = fetch,
): Promise<string[]> {
  try {
    const res = await doFetch(`${baseUrl.replace(/\/+$/, '')}/models`, {
      headers: apiKey ? { Authorization: `Bearer ${apiKey}` } : {},
    });
    if (!res.ok) return [];
    const body = (await res.json()) as { data?: { id?: string }[] };
    return (body.data ?? [])
      .map((m) => m.id ?? '')
      .filter(Boolean)
      .sort();
  } catch {
    return [];
  }
}
